import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddDentistFormComponent from './add-dentist-form.component';
import { User } from 'models/user';
import { useFormik } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { getUserByEmailTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { LoadingButton } from '@mui/lab';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Organization } from 'models/organization';
import LinkUserFormComponent from '../../shared/link-user-form.component';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';

type AddDentistDialogProps = {
    dialogOpen: boolean;
    onDialogClose: () => void;
    onUserAdded: (user: User) => void;
    onUserLinked?: (user: User) => void;
};

const AddDentistDialog: FC<AddDentistDialogProps> = ({
    dialogOpen,
    onDialogClose,
    onUserAdded,
    onUserLinked,
}: AddDentistDialogProps) => {
    const { t: translate } = useTranslation('users');
    const [activeStep, setActiveStep] = useState(0);
    const [notAllowedMsg, setNotAllowedMsg] = useState('');
    const [user, setUser] = useState<User>({} as User);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const [isIsolatedUsersEnable, setIsIsolatedUsersEnable] = useState(false);

    const [getUserByEmail, { loading }] = useLazyQuery(getUserByEmailTemplate, {
        onCompleted: ({ userByEmail: user }) => {
            if (!user) {
                setActiveStep(1);
                return;
            }
            setUser(user);
            if (user.organization?._id === currentOrganization._id) {
                // User already exists in organization
                setActiveStep(3);
                return;
            }
            if (user.isShared) {
                // No allowed
                setNotAllowedMsg('NOT_ALLOWED_SHARED');
                setActiveStep(4);
                return;
            }
            const isDentistOrExternalTech =
                permissions.isDentist(user.type) ||
                permissions.isLabTechnician(user.type) ||
                permissions.isExternalTechnician(user.role);
            if (permissions.isWhiteLabeledOrganization(currentUser?.organization as Organization)) {
                if (isDentistOrExternalTech) {
                    // Link
                    const hasIsolatedUsers = customerSettings.getCustomerSetting(
                        user.organization.customerSettings || [],
                        CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
                    );
                    if (hasIsolatedUsers) {
                        // No allowed
                        setNotAllowedMsg('NOT_ALLOWED_CLINIC');
                        setActiveStep(4);
                        return;
                    }
                    setActiveStep(2);
                    return;
                }
            } else {
                if (
                    isDentistOrExternalTech ||
                    (permissions.isWhiteLabeledOrganization(user.organization) && permissions.isDentist(user.type))
                ) {
                    // Link
                    const hasIsolatedUsers = customerSettings.getCustomerSetting(
                        user.organization.customerSettings || [],
                        CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
                    );
                    if (hasIsolatedUsers) {
                        // No allowed
                        setNotAllowedMsg('NOT_ALLOWED_CLINIC');
                        setActiveStep(4);
                        return;
                    }
                    setActiveStep(2);
                    return;
                }
            }
            // No allowed
            setNotAllowedMsg('NOT_ALLOWED');
            setActiveStep(4);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: { email: string }) => {
        const errors: Record<string, string> = {};
        if (!values.email) {
            errors.email = translate('users:VALIDATION:EMAIL');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validate,
        onSubmit: (values: { email: string }) => {
            getUserByEmail({ variables: { email: values.email } });
        },
    });

    const moveToFirstStep = () => {
        setActiveStep(0);
    };

    const userLinked = (user: User) => {
        onDialogClose();
        if (onUserLinked) {
            onUserLinked(user);
        }
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return (
                    <Fragment>
                        <form onSubmit={formik.handleSubmit}>
                            <DialogContent>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="email"
                                        label={translate('common:EMAIL')}
                                        required
                                        type="email"
                                        helperText={formik.touched.email && formik.errors.email}
                                        error={formik.touched.email && Boolean(formik.errors.email)}
                                        {...formik.getFieldProps('email')}
                                    />
                                </FormControl>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                                <LoadingButton type="submit" variant="contained" loading={loading}>
                                    {translate('common:NEXT_STEP')}
                                </LoadingButton>
                            </DialogActions>
                        </form>
                    </Fragment>
                );
            case 1:
                return (
                    <AddDentistFormComponent
                        onUserAdded={onUserAdded}
                        onDialogClose={onDialogClose}
                        email={formik.values.email}
                        isIsolatedUsersEnable={isIsolatedUsersEnable}
                    />
                );
            case 2:
                return (
                    <LinkUserFormComponent
                        userType="DENTIST"
                        user={user}
                        onBack={moveToFirstStep}
                        onUserLinked={userLinked}
                    />
                );
            case 3:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate(`users:USER_ALREADY_EXISTS`)}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                            <Button onClick={moveToFirstStep} variant="contained">
                                {translate('common:BACK')}
                            </Button>
                        </DialogActions>
                    </DialogContent>
                );
            case 4:
                return (
                    <DialogContent>
                        <Box>
                            <Alert severity="warning">{translate(`users:${notAllowedMsg}`)}</Alert>
                        </Box>
                        <DialogActions>
                            <Button onClick={() => onDialogClose()}>{translate('common:CANCEL')}</Button>
                            <Button onClick={moveToFirstStep} variant="contained">
                                {translate('common:BACK')}
                            </Button>
                        </DialogActions>
                    </DialogContent>
                );
            default:
                return 'Unknown Step';
        }
    };

    useEffect(() => {
        // Reset on dialog open
        if (dialogOpen) {
            formik.resetForm();
            setNotAllowedMsg('');
            setUser({} as User);
            const hasIsolatedUsers = customerSettings.getCustomerSetting(
                currentOrganization.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ISOLATED_USERS,
            );
            if (hasIsolatedUsers) {
                setActiveStep(1);
                setIsIsolatedUsersEnable(true);
            } else {
                setActiveStep(0);
                setIsIsolatedUsersEnable(false);
            }
        }
    }, [dialogOpen]);

    return (
        <Dialog maxWidth="md" fullWidth open={dialogOpen} onClose={onDialogClose}>
            <DialogTitle>{translate('users:ADD_DENTIST')}</DialogTitle>
            {getStepContent(activeStep)}
        </Dialog>
    );
};

export default AddDentistDialog;
